import { supabase } from '@/core/database/supabase';
import { estimateHeadersService } from './estimateHeaders.service';
import { estimateItemsService } from './estimateItems.service';

export const estimateConvertBoqService = {
  async convertToBoq(estimateId: string): Promise<{ boqId: string; itemCount: number }> {
    const estimate = await estimateHeadersService.getById(estimateId);
    if (!estimate) throw new Error('Không tìm thấy dự toán để chuyển BOQ.');

    if (!['approved', 'locked', 'converted_to_boq'].includes(String(estimate.status_code))) {
      throw new Error('Chỉ dự toán đã duyệt hoặc đã khóa mới được chuyển BOQ.');
    }
    if (!estimate.project_id) {
      throw new Error('Dự toán cần gắn project trước khi chuyển BOQ.');
    }

    const { items } = await estimateItemsService.getByEstimateId(estimateId);

    const { data: boq, error: boqError } = await supabase
      .from('construction_boqs')
      .insert({
        company_id: estimate.company_id,
        project_id: estimate.project_id,
        boq_code: estimate.estimate_code.replace(/^EST/i, 'BOQ'),
        boq_name: estimate.estimate_name,
        description: estimate.description,
        status_code: 'draft',
        version_no: estimate.version_no || 1,
        total_amount: estimate.total_amount || 0,
      })
      .select('id')
      .single();

    if (boqError) throw boqError;

    if (items.length > 0) {
      const boqItems = items.map((item) => ({
        company_id: item.company_id,
        project_id: item.project_id || estimate.project_id!,
        boq_id: boq.id,
        parent_id: null,
        zone_id: item.zone_id || null,
        phase_id: item.phase_id || null,
        category_code: item.category_code,
        item_code: item.item_code,
        item_name: item.item_name,
        description: item.description,
        specification: item.specification,
        unit: item.unit,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_amount: item.total_amount,
        material_cost: item.material_cost,
        labor_cost: item.labor_cost,
        equipment_cost: item.equipment_cost,
        subcontract_cost: item.subcontract_cost,
        waste_factor: item.waste_factor,
        status_code: 'planned',
        sort_order: item.sort_order,
      }));

      const { data: createdItems, error: itemsError } = await supabase
        .from('construction_boq_items')
        .insert(boqItems)
        .select('id');

      if (itemsError) throw itemsError;

      const links = (createdItems || []).map((createdItem, index) => ({
        company_id: estimate.company_id,
        project_id: estimate.project_id,
        estimate_id: estimate.id,
        estimate_item_id: items[index]?.id || null,
        boq_id: boq.id,
        boq_item_id: createdItem.id,
      }));

      if (links.length > 0) {
        const { error: linksError } = await supabase.from('estimate_to_boq_links' as any).insert(links);
        if (linksError && linksError.code !== '42P01') throw linksError;
      }
    }

    if (estimate.source === 'estimate_headers') {
      const { error } = await supabase
        .from('estimate_headers' as any)
        .update({ status_code: 'converted_to_boq', updated_at: new Date().toISOString() })
        .eq('id', estimate.id);
      if (error && error.code !== '42P01') throw error;
    }

    return { boqId: boq.id, itemCount: items.length };
  },
};
